import type { ReactNode } from 'react';
import { cx } from '../cx';

export type IcebergPart = { id: string; label: ReactNode; value: number; /** Formatted amount. */ display: ReactNode };

export type IcebergProps = {
  /** What you take home: above the waterline, in sun. */
  net: IcebergPart;
  /** Taxes and contributions from gross pay: just below the waterline, in ink. */
  taxes: IcebergPart[];
  /** What the employer pays on top of gross: deepest, in iris. */
  employerCosts?: IcebergPart[];
  /** The waterline's label ("Your gross pay"). */
  waterline: ReactNode;
  title: ReactNode;
  /** The assumptions behind the figures, all listed. */
  assumptions?: ReactNode;
  className?: string;
};

/** Each part's share of the iceberg's height, in percent. Pure, exported for tests. */
export function icebergShares(parts: Array<{ value: number }>): number[] {
  const total = parts.reduce((s, p) => s + Math.max(0, p.value), 0) || 1;
  return parts.map((p) => Math.round((Math.max(0, p.value) / total) * 1000) / 10);
}

/**
 * The tax iceberg: take-home pay above the waterline, the taxes taken from gross pay below it,
 * and the employer's costs underneath. Heights are proportional; every part is labelled with its
 * amount, and the figures come from the calculator, never typed.
 */
export function Iceberg({ net, taxes, employerCosts = [], waterline, title, assumptions, className }: IcebergProps) {
  const parts = [
    { part: net, cls: 'bg-jc-cta text-jc-on-cta' },
    ...taxes.map((part) => ({ part, cls: 'bg-jc-ink text-jc-surface' })),
    ...employerCosts.map((part) => ({ part, cls: 'bg-jc-forest text-jc-on-forest' })),
  ];
  const shares = icebergShares(parts.map((p) => p.part));
  return (
    <figure className={cx('m-0 flex flex-col gap-3', className)}>
      <figcaption className="type-subheading text-jc-ink">{title}</figcaption>
      <div className="flex flex-col">
        {parts.map(({ part, cls }, i) => (
          <div key={part.id} className="flex flex-col">
            {i === 1 ? (
              <div className="flex items-center gap-2 py-1.5 type-eyebrow text-jc-ink-secondary">
                <span aria-hidden="true" className="h-0.5 flex-1 bg-jc-accent" />
                {waterline}
                <span aria-hidden="true" className="h-0.5 flex-1 bg-jc-accent" />
              </div>
            ) : null}
            <div
              className={cx(
                'flex min-h-9 items-center justify-between gap-3 px-4 text-sm font-semibold',
                cls,
                i === 0 && 'rounded-t-jc-info',
                i === parts.length - 1 && 'rounded-b-jc-info',
              )}
              style={{ height: `${Math.max(2.25, shares[i] * 0.2)}rem` }}
            >
              <span>{part.label}</span>
              <span className="font-display font-bold tabular-nums">{part.display}</span>
            </div>
          </div>
        ))}
      </div>
      {assumptions ? <div className="text-xs text-jc-ink-secondary">{assumptions}</div> : null}
    </figure>
  );
}
